/**
 * Centralized error tracking for KnowledgeBase plugins.
 * Provides proper logging, user notification, and error inspection.
 */

const errors = []
const listeners = new Set()

const isDev = import.meta.env?.DEV ?? process.env.NODE_ENV !== 'production'

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
}

/**
 * Notify all registered listeners of errors
 */
const notifyListeners = () => {
  for (const fn of listeners) {
    try {
      fn(errors.slice())
    } catch (e) {
      // Don't let listener errors break the notification chain
      if (isDev) console.warn('[KnowledgeBase] Error listener threw:', e)
    }
  }
}

/**
 * Track an error from plugin loading or execution
 * @param {Object} options - Error details
 * @param {string} options.source - The plugin or component that threw the error
 * @param {string} options.phase - When the error occurred (load, register, execute)
 * @param {Error} options.error - The original error object
 * @param {string} [options.severity] - Error severity level
 * @param {Object} [options.context] - Additional context about the error
 */
export function trackError({ source, phase, error, severity = ErrorSeverity.ERROR, context = {} }) {
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    source,
    phase,
    severity,
    message: error?.message || String(error),
    stack: error?.stack || null,
    context,
    timestamp: new Date().toISOString()
  }

  errors.push(entry)

  // Log to console in development
  if (isDev) {
    const logMethod =
      severity === ErrorSeverity.CRITICAL
        ? 'error'
        : severity === ErrorSeverity.WARNING
          ? 'warn'
          : 'error'
    console[logMethod](`[KnowledgeBase] Plugin ${phase} error in "${source}":`, error, context)
  }

  notifyListeners()
  return entry
}

/**
 * Wrap a function to automatically track errors
 * @param {string} source - The source name for error tracking
 * @param {string} phase - The phase (load, register, execute)
 * @param {Function} fn - The function to wrap
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.rethrow] - Whether to rethrow the error after tracking
 * @param {string} [options.severity] - Default severity for caught errors
 * @returns {Function} Wrapped function
 */
export function withErrorTracking(source, phase, fn, options = {}) {
  const { rethrow = false, severity = ErrorSeverity.ERROR } = options

  return function (...args) {
    try {
      const result = fn.apply(this, args)
      // Handle async functions
      if (result && typeof result.then === 'function') {
        return result.catch((error) => {
          trackError({ source, phase, error, severity })
          if (rethrow) throw error
          return undefined
        })
      }
      return result
    } catch (error) {
      trackError({ source, phase, error, severity })
      if (rethrow) throw error
      return undefined
    }
  }
}

/**
 * Execute a function with error tracking (useful for one-off calls)
 * @param {string} source - The source name
 * @param {string} phase - The phase
 * @param {Function} fn - The function to execute
 * @param {Object} [options] - Additional options
 * @returns {*} The result of the function, or undefined on error
 */
export function safeExecute(source, phase, fn, options = {}) {
  return withErrorTracking(source, phase, fn, options)()
}

/**
 * Get all tracked errors
 * @returns {Array} List of error entries
 */
export function getErrors() {
  return errors.slice()
}

/**
 * Get errors filtered by source
 * @param {string} source - The source to filter by
 * @returns {Array} Filtered error list
 */
export function getErrorsBySource(source) {
  return errors.filter((e) => e.source === source)
}

/**
 * Get errors filtered by phase
 * @param {string} phase - The phase to filter by
 * @returns {Array} Filtered error list
 */
export function getErrorsByPhase(phase) {
  return errors.filter((e) => e.phase === phase)
}

/**
 * Get errors filtered by severity
 * @param {string} severity - The severity to filter by
 * @returns {Array} Filtered error list
 */
export function getErrorsBySeverity(severity) {
  return errors.filter((e) => e.severity === severity)
}

/**
 * Check if any critical errors occurred
 * @returns {boolean}
 */
export function hasCriticalErrors() {
  return errors.some((e) => e.severity === ErrorSeverity.CRITICAL)
}

/**
 * Check if any errors occurred during plugin loading
 * @returns {boolean}
 */
export function hasLoadErrors() {
  return errors.some((e) => e.phase === 'load')
}

/**
 * Get a summary of all errors for display
 * @returns {Object} Summary object
 */
export function getErrorSummary() {
  const bySource = {}
  const bySeverity = {
    [ErrorSeverity.WARNING]: 0,
    [ErrorSeverity.ERROR]: 0,
    [ErrorSeverity.CRITICAL]: 0
  }

  for (const err of errors) {
    bySource[err.source] = (bySource[err.source] || 0) + 1
    bySeverity[err.severity] = (bySeverity[err.severity] || 0) + 1
  }

  return {
    total: errors.length,
    bySource,
    bySeverity,
    hasCritical: bySeverity[ErrorSeverity.CRITICAL] > 0
  }
}

/**
 * Clear all tracked errors
 */
export function clearErrors() {
  errors.length = 0
  notifyListeners()
}

/**
 * Subscribe to error changes
 * @param {Function} callback - Called when errors change
 * @returns {Function} Unsubscribe function
 */
export function onErrorsChange(callback) {
  if (typeof callback !== 'function') return () => {}
  listeners.add(callback)
  return () => listeners.delete(callback)
}

export default {
  ErrorSeverity,
  trackError,
  withErrorTracking,
  safeExecute,
  getErrors,
  getErrorsBySource,
  getErrorsByPhase,
  getErrorsBySeverity,
  hasCriticalErrors,
  hasLoadErrors,
  getErrorSummary,
  clearErrors,
  onErrorsChange
}
